import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import { Gift, UserPlus, Users, Coins } from 'lucide-react';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import { useReferrals } from './hooks/useReferrals';
import { useSaveReferral } from './hooks/useSaveReferral';

const STATUS_CONFIG = {
  pending: { label: 'Aguardando', color: 'warning' },
  active: { label: 'Ativo', color: 'success' },
  credited: { label: 'Crédito aplicado', color: 'info' },
};

export default function BenefitsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [whatsApp, setWhatsApp] = useState('');

  const { referrals, isLoading: isReferralsLoading, refetch: fetchReferrals } = useReferrals();
  const { save: saveReferral } = useSaveReferral();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const credits = referrals.filter((r) => r.status === 'credited').length;

  const handleSubmit = () => {
    if (!name.trim() || !whatsApp.trim()) return;
    setLoading(true);
    saveReferral({ name, whatsApp })
      .then(() => {
        setSuccessMessage('Indicação enviada com sucesso!');
        setShowSuccessMessage(true);
        setName('');
        setWhatsApp('');
        setModalOpen(false);
        fetchReferrals();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setName('');
    setWhatsApp('');
    setModalOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', py: 2 }}>
      <Loading isToHidden={isLoading === false && isReferralsLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      {/* Hero */}
      <Card
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <Gift size={24} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Indique e ganhe mensalidade grátis ou brindes
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 600, lineHeight: 1.7 }}
              >
                Conhece algum profissional que ainda controla tudo no papel ou em planilhas?
                Indique-o para o SIGO e, assim que ele começar a usar a plataforma, você acumula
                créditos que podem ser usados para <strong>abater mensalidades</strong> ou trocar
                por <strong>brindes exclusivos</strong> — a escolha é sua. Não há limite de
                indicações, cada colega que ativar uma conta gera um novo crédito. Se o seu plano
                for anual, os créditos de mensalidade são aplicados automaticamente na próxima
                renovação.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<UserPlus size={18} />}
            onClick={() => setModalOpen(true)}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Indicar colega
          </Button>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Créditos disponíveis */}
        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Coins size={18} color="#1976d2" />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Créditos disponíveis
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {credits}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {credits === 1 ? 'mês gratuito ou brinde' : 'meses gratuitos ou brindes'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total de indicações */}
        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Users size={18} color="#1976d2" />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Total de indicações
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {referrals.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {referrals.filter((r) => r.status === 'credited').length} crédito(s) já aplicado(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de indicações */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Minhas indicações
          </Typography>

          {referrals.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Você ainda não fez nenhuma indicação.
            </Typography>
          ) : (
            referrals.map((referral, index) => {
              const status = STATUS_CONFIG[referral.status] ?? {
                label: referral.status ?? 'Desconhecido',
                color: 'default',
              };
              return (
                <React.Fragment key={referral.id}>
                  {index > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {referral.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {referral.whatsAp} · Indicado em {referral.createdAt}
                      </Typography>
                    </Box>
                    <Chip label={status.label} color={status.color} size="small" />
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Modal de indicação */}
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Indicar colega profissional</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
        >
          <TextField
            label="Nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Telefone / WhatsApp"
            value={whatsApp}
            onChange={(e) => setWhatsApp(e.target.value)}
            fullWidth
            placeholder="(00) 00000-0000"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!name.trim() || !whatsApp.trim()}
          >
            Enviar indicação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
